import * as React from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import SupportIcon from '@mui/icons-material/Support';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ChatIcon from '@mui/icons-material/Chat';
// import Buy from './Buy';
// import Sell from './Sell';
// import BlogSlider from '../Blog/BlogSlider';

export default function P2P() {
  const [value, setValue] = React.useState('Buy');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleContactSupport = () => {
    // Function to try opening Smartsupp
    const tryOpenSmartsupp = () => {
      try {
        // Method 1: Use the Smartsupp API directly
        if (window.smartsupp && typeof window.smartsupp === 'function') {
          window.smartsupp('open');
          return true;
        }
        
        // Method 2: Try the _smartsupp object
        if (window._smartsupp && typeof window._smartsupp.open === 'function') {
          window._smartsupp.open();
          return true;
        }
        
        // Method 3: Try to find and click the Smartsupp widget button
        const smartsuppButton = document.querySelector('[data-smartsupp-widget]') || 
                               document.querySelector('.smartsupp-widget') ||
                               document.querySelector('[id*="smartsupp"]') ||
                               document.querySelector('[class*="smartsupp"]') ||
                               document.querySelector('iframe[src*="smartsupp"]');
        
        if (smartsuppButton) {
          smartsuppButton.click();
          return true;
        }
        
        // Method 4: Try to trigger via postMessage for iframe
        const iframes = document.querySelectorAll('iframe');
        iframes.forEach(iframe => {
          if (iframe.src && iframe.src.includes('smartsupp')) {
            try {
              iframe.contentWindow.postMessage({ type: 'open' }, '*');
            } catch (e) {
              console.log('Could not post message to iframe:', e);
            }
          }
        });
        
        // Method 5: Try to dispatch a custom event that Smartsupp might listen to
        window.dispatchEvent(new CustomEvent('smartsupp-open'));
        
        // Method 6: Try to find any clickable element that might be the chat button
        const possibleChatButtons = document.querySelectorAll('button, div, span');
        for (let element of possibleChatButtons) {
          const style = window.getComputedStyle(element);
          if (style.position === 'fixed' && 
              (element.textContent.toLowerCase().includes('chat') || 
               element.textContent.toLowerCase().includes('support') ||
               element.className.toLowerCase().includes('chat'))) {
            element.click();
            return true;
          }
        }
        
        return false;
      } catch (error) {
        console.error('Error opening Smartsupp chat:', error);
        return false;
      }
    };

    // Try immediately
    if (tryOpenSmartsupp()) {
      return;
    }

    // If immediate attempt fails, wait a bit and try again (Smartsupp might still be loading)
    setTimeout(() => {
      if (tryOpenSmartsupp()) {
        return;
      }
      
      // If still fails, wait a bit more and try one more time
      setTimeout(() => {
        if (tryOpenSmartsupp()) {
          return;
        }
        
        // Final fallback: Show a helpful message
        alert('Please look for the chat widget in the bottom right corner of the page to contact our support team.');
      }, 1000);
    }, 500);
  };

  return (
    <Box sx={{ width: '100%', typography: 'body1', marginTop: '75px' }}>
      <Typography 
        variant="h4" 
        component="h1" 
        gutterBottom 
        sx={{ 
          fontWeight: 'bold',
          color: theme => theme.palette.mode === 'dark' ? '#90caf9' : '#1976D2',
          mb: 3
        }}
      >
        P2P Trading
      </Typography>
        
      {/* <BlogSlider/> */}
      
      {/* P2P Contact Support Section */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: isMobile ? 3 : 4, 
          mb: 4,
          borderRadius: 3,
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%)'
            : 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(76, 175, 80, 0.05) 100%)',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)'}`,
        }}
      >
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SecurityIcon 
                sx={{ 
                  fontSize: 32, 
                  color: 'primary.main', 
                  mr: 2,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                }} 
              />
              <Typography 
                variant="h5" 
                component="h2" 
                sx={{ 
                  fontWeight: 700,
                  color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main'
                }}
              >
                Verified P2P Trading
              </Typography>
            </Box>
            
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 3, 
                fontSize: '1.1rem',
                lineHeight: 1.6,
                color: theme.palette.mode === 'dark' ? 'text.primary' : 'text.secondary'
              }}
            >
              For secure and verified P2P trading, please contact our support team to get your account verified and access our trusted network of verified traders. Our team will guide you through the verification process and provide you with secure trading options.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <VerifiedUserIcon sx={{ color: 'success.main', mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Verified Traders Only
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SecurityIcon sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Secure Transactions
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SupportIcon sx={{ color: 'info.main', mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  24/7 Support
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              size="large"
              startIcon={<ChatIcon />}
              onClick={handleContactSupport}
              sx={{
                background: 'linear-gradient(135deg, #1976D2 0%, #4CAF50 100%)',
                color: 'white',
                fontWeight: 600,
                py: 1.5,
                px: 4,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1565C0 0%, #388E3C 100%)',
                  boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  transform: 'translateY(-2px)',
                  transition: 'all 0.3s ease'
                },
                fontSize: isMobile ? '1rem' : '1.1rem'
              }}
            >
              Contact Live Chat
            </Button>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Box sx={{ 
              textAlign: 'center',
              p: 3,
              borderRadius: 2,
              background: theme.palette.mode === 'dark' 
                ? 'rgba(76, 175, 80, 0.1)' 
                : 'rgba(76, 175, 80, 0.05)',
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)'}`,
            }}>
              <SupportIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Get Verified Today
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Our support team is ready to help you get started with secure P2P trading
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Commented out original P2P functionality */}
      {/*
      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleChange} aria-label="P2P Transaction" 
          variant="scrollable"
          gap={1}
          scrollButtons={true}
          >
            <Tab label="Buy" value="Buy" />            
            <Tab label="Sell" value="Sell" />
          </TabList>
        </Box>
        <TabPanel value="Buy"><Buy/></TabPanel>
        <TabPanel value="Sell"><Sell/></TabPanel>
        
      </TabContext>
      */}
    </Box>
  );
}
